import React from "react";
import { Alert, Box, Collapse } from "@mui/material";

type ToastSeverity = "success" | "error" | "info" | "warning";

type ToastItem = {
  id: number;
  message: string;
  severity: ToastSeverity;
  duration: number;
};

type ToastContextValue = {
  showToast: (
    message: string,
    options?: { severity?: ToastSeverity; duration?: number },
  ) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
};

const ToastContext = React.createContext<ToastContextValue | undefined>(
  undefined,
);

const DEFAULT_DURATION = 4000;

const ToastAlert: React.FC<{
  toast: ToastItem;
  onClose: (id: number) => void;
}> = ({ toast, onClose }) => {
  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      onClose(toast.id);
    }, toast.duration);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [onClose, toast.duration, toast.id]);

  return (
    <Collapse in appear>
      <Alert
        severity={toast.severity}
        variant="filled"
        onClose={() => onClose(toast.id)}
        sx={{
          minWidth: { xs: "min(100vw - 32px, 320px)", sm: 360 },
          boxShadow: "0 14px 32px rgba(15, 23, 42, 0.18)",
          alignItems: "center",
          pointerEvents: "auto",
        }}
      >
        {toast.message}
      </Alert>
    </Collapse>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const removeToast = React.useCallback((id: number) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  }, []);

  const showToast = React.useCallback(
    (
      message: string,
      options?: { severity?: ToastSeverity; duration?: number },
    ) => {
      const trimmedMessage = String(message || "").trim();
      if (!trimmedMessage) return;

      setToasts((currentToasts) => [
        ...currentToasts,
        {
          id: Date.now() + Math.floor(Math.random() * 1000),
          message: trimmedMessage,
          severity: options?.severity || "info",
          duration: options?.duration || DEFAULT_DURATION,
        },
      ]);
    },
    [],
  );

  const value = React.useMemo<ToastContextValue>(
    () => ({
      showToast,
      success: (message, duration) =>
        showToast(message, { severity: "success", duration }),
      error: (message, duration) =>
        showToast(message, { severity: "error", duration }),
      info: (message, duration) =>
        showToast(message, { severity: "info", duration }),
      warning: (message, duration) =>
        showToast(message, { severity: "warning", duration }),
    }),
    [showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Box
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.snackbar,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          pointerEvents: "none",
        }}
      >
        {toasts.map((toast) => (
          <ToastAlert key={toast.id} toast={toast} onClose={removeToast} />
        ))}
      </Box>
    </ToastContext.Provider>
  );
};

export const useToastContext = (): ToastContextValue => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};
